import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { forkJoin, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { ApiHttp, SCHEMA_VERSION } from '@core/api/api-http';
import { ReassignmentRequest } from '@core/api/reassignment-request';
import { environment } from '@env/environment';
import { ContactSummary } from '@features/contacts/models';
import { ContactService } from '@features/contacts/services/contact.service';
import { ConvertLeadRequest } from '@features/leads/models/convert-lead-request';
import { LeadSummary } from '@features/leads/models/leadsummary';
import { TerminateLeadRequest } from '@features/leads/models/terminate-lead-request';

import { ConvertLeadResponse } from '../models/convert-lead-response';
import { CreateOpportunityRequest } from '../models/create-opportunity-request';
import { CreateOpportunityResponse } from '../models/create-opportunity-response';
import { Lead } from '../models/lead';

export interface LeadSearchFilterOptions {
  search?: string;
  status?: string;
  includeAccessible?: boolean;
  /** When set, list is filtered client-side to leads returned for this tag from the Clients tags API. */
  tagId?: string;
}

function getSearchParams(options: LeadSearchFilterOptions) {
  let params: any = {};
  if (options.search) {
    params.Search = options.search;
  }

  if (options.status) {
    params.Status = options.status;
  }

  if (_.isBoolean(options.includeAccessible)) {
    params.includeAccessible = options.includeAccessible;
  }

  return Object.keys(params) ? params : null;
}

@Injectable({
  providedIn: 'root'
})
export class LeadService {
  replacer(key: string, value: any): any {
    if ((typeof value === 'string' && value === '') || value === null)
      return undefined;
    else return value;
  }

  constructor(
    private http: ApiHttp,
    private readonly contactService: ContactService
  ) {}

  summary(id: string): Observable<LeadSummary> {
    return this.http.getEntity<LeadSummary>(
      environment.clientsApiUrl,
      `leads/${id}`,
      SCHEMA_VERSION.LEAD,
      {
        headers: ApiHttp.getStandardOptions().headers,
        params: {
          depth: 1
        }
      }
    );
  }

  getLeads(options: LeadSearchFilterOptions): Observable<LeadSummary[]> {
    const params = getSearchParams(options);

    let httpOptions = {
      headers: ApiHttp.getStandardOptions().headers,
      params
    };

    return this.http.getEntity(
      environment.clientsApiUrl,
      'leads',
      SCHEMA_VERSION.LEAD,
      httpOptions
    );
  }

  getLeadForDisplay(id: string): Observable<Lead.Model> {
    return this.http
      .getEntity<Lead.SchemaContract>(
        environment.clientsApiUrl,
        `leads/${id}`,
        SCHEMA_VERSION.LEAD,
        ApiHttp.getStandardOptions()
      )
      .pipe(map((x) => Lead.toModel(x)));
  }

  getLeadWithContacts(
    id: string
  ): Observable<{ lead: Lead.Model; contacts: ContactSummary[] }> {
    return this.getLeadForDisplay(id).pipe(
      switchMap((lead: Lead.Model) => {
        const contactIds = [];
        if (lead.existingContactId) {
          contactIds.push(lead.existingContactId);
        }
        if (
          lead.referrer &&
          'referrerId' in lead.referrer &&
          lead.referrer.referrerId
        ) {
          contactIds.push(lead.referrer.referrerId);
        }
        return this.contactService
          .getSpecificContacts(contactIds)
          .pipe(map((contacts) => ({ lead, contacts })));
      })
    );
  }

  getLeadDefinition(): Observable<Lead.SchemaPropertyDefinition> {
    return this.http.getEntity(
      environment.clientsApiUrl,
      'leads/schema',
      SCHEMA_VERSION.LEAD,
      ApiHttp.getStandardOptions()
    );
  }

  updateLead(leadId: string, change: Partial<Lead.Model>): Observable<any> {
    return this.http.put(
      environment.clientsApiUrl,
      `leads/${leadId}`,
      Lead.toSchemaContract(change),
      SCHEMA_VERSION.LEAD,
      ApiHttp.getStandardOptions()
    );
  }

  convertToContact(
    lead: ConvertLeadRequest,
    id: string
  ): Observable<ConvertLeadResponse> {
    return this.http.postEntity<ConvertLeadResponse>(
      environment.clientsApiUrl,
      `leads/${id}/convert-to-contact`,
      JSON.stringify(lead, this.replacer),
      SCHEMA_VERSION.LEAD,
      ApiHttp.getStandardOptions()
    );
  }

  convertToOpportunity(
    data: CreateOpportunityRequest.Model,
    id: String
  ): Observable<CreateOpportunityResponse> {
    return this.http.postEntity<CreateOpportunityResponse>(
      environment.clientsApiUrl,
      `leads/${id}/convert-to-opportunity`,
      CreateOpportunityRequest.toContract(data),
      SCHEMA_VERSION.LEAD,
      ApiHttp.getStandardOptions()
    );
  }

  terminateLead(lead: TerminateLeadRequest, id: String): Observable<null> {
    return this.http.postEntity(
      environment.clientsApiUrl,
      `leads/${id}/terminate`,
      JSON.stringify(lead, this.replacer),
      SCHEMA_VERSION.LEAD,
      ApiHttp.getStandardOptions()
    );
  }

  reassignLead(lead: ReassignmentRequest, id: String): Observable<null> {
    return this.http.postEntity(
      environment.clientsApiUrl,
      `reassign/Enquiry/${id}`,
      JSON.stringify(lead, this.replacer),
      SCHEMA_VERSION.LEAD,
      ApiHttp.getStandardOptions()
    );
  }

  linkLeadToContact(leadId: String, personId: String) {
    return this.http.postEntity(
      environment.clientsApiUrl,
      `leads/${leadId}/link-to-contact/${personId}`,
      {},
      SCHEMA_VERSION.LEAD,
      ApiHttp.getStandardOptions()
    );
  }

  unlinkLeadFromContact(leadId: String) {
    return this.http.postEntity(
      environment.clientsApiUrl,
      `leads/${leadId}/unlink-from-contact`,
      {},
      SCHEMA_VERSION.LEAD,
      ApiHttp.getStandardOptions()
    );
  }

  getLeadsByPortfolio(portfolioId: string): Observable<LeadSummary[]> {
    const option: LeadSearchFilterOptions = {
      status: Lead.Status.Open,
      search: ''
    };
    return this.getLeads(option).pipe(
      map((leads) => leads.filter((o) => o.portfolio === portfolioId))
    );
  }

  reassign(leadId: string, portfolioId: string): Observable<object> {
    return this.http.postEntity(
      environment.clientsApiUrl,
      `leads/${leadId}/reassign/${portfolioId}`,
      {},
      SCHEMA_VERSION.LEAD,
      ApiHttp.getStandardOptions()
    );
  }

  reassignAll(leadIds: string[], portfolioId: string): Observable<object> {
    return forkJoin(
      leadIds.map(
        (o) => this.reassign(o, portfolioId)
        // TODO: Need to show detailed error messages in UI
        // .pipe(
        //   map(() => ({ opportunityId: o, success: true })),
        //   catchError(() => of({ opportunityId: o, success: false })))
      )
    );
  }
}
